/**
 * @fileoverview Projects the student can pick on the standalone tracker page.
 *
 * Reuses `GET /api/arc_mosaico/projects` (same call as the student dashboard
 * and "My tasks"): for the student profile the backend scopes it through
 * `project_students` and only returns `approved` and `finished` projects —
 * exactly the statuses the tracker endpoint accepts.
 */

import { useEffect, useState } from 'react'
import { fetchMyProjects } from 'pages/ArcProjectsList/services/arcProjectsApi'
import { PROJECTS_PAGE_LIMIT } from '../constants'
import type { StudentTrackerProjectOption } from '../types'

const NATURAL_SORT: Intl.CollatorOptions = { numeric: true, sensitivity: 'base' }

interface UseStudentProjectsReturn {
  projects: StudentTrackerProjectOption[]
  loading: boolean
  error: boolean
}

export function useStudentProjects(): UseStudentProjectsReturn {
  const [projects, setProjects] = useState<StudentTrackerProjectOption[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  useEffect(() => {
    let cancelled = false
    fetchMyProjects({ limit: PROJECTS_PAGE_LIMIT })
      .then(result => {
        if (cancelled) return
        const options = result.projects
          // Some names come back with leading spaces, which would sort them first — trim before sorting.
          .map(project => ({ id: project.id, name: project.name.trim(), status: project.status }))
          .sort((a, b) => a.name.localeCompare(b.name, undefined, NATURAL_SORT))
        setProjects(options)
      })
      .catch(() => {
        if (!cancelled) setError(true)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  return { projects, loading, error }
}
